import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import { PageReadyProvider } from './context/PageReadyContext';
import Footer from './components/layout/Footer';
import Navbar from './components/layout/Navbar';
import RouteTransition from './components/layout/RouteTransition';
import Hero from './components/home/Hero';
import Products from './components/home/Products';
import About from './components/home/About';
import Contact from './components/home/Contact';
import ProductPage from './components/products/ProductPage';
import { getProductById } from './components/products/productData';
import ContactRoutePage from './components/pages/ContactPage';
import {
  AboutRoutePage,
  CategoryRoutePage,
  FaqRoutePage,
  ProductsRoutePage,
  SupportRoutePage,
} from './components/pages/SitePages';

const pageVariants = {
  initial: {
    opacity: 0,
    y: 8,
  },
  animate: {
    opacity: 1,
    y: 0,
  },
  exit: {
    opacity: 0,
    y: -6,
  },
};

const pageTransition = {
  duration: 0.34,
  ease: [0.4, 0, 0.2, 1],
};

const brandName = 'Sonny Blind';

const getPageTitle = (pathname) => {
  if (pathname === '/') {
    return brandName;
  }

  if (pathname === '/products') {
    return `${brandName} - Products & Services`;
  }

  if (pathname === '/about') {
    return `${brandName} - About Us`;
  }

  if (pathname === '/contact') {
    return `${brandName} - Contact Us`;
  }

  if (pathname === '/faq') {
    return `${brandName} - FAQ`;
  }

  if (pathname === '/support') {
    return `${brandName} - Customer Support`;
  }

  if (pathname === '/blinds') {
    return `${brandName} - Blinds`;
  }

  if (pathname === '/shutters') {
    return `${brandName} - Shutters`;
  }

  if (pathname === '/shades') {
    return `${brandName} - Shades`;
  }

  if (pathname.startsWith('/product/')) {
    const productId = pathname.split('/')[2];
    const product = getProductById(productId);

    return `${product?.name || 'Product'} - ${brandName}`;
  }

  return brandName;
};

function AppShell({ isMenuOpen, setIsMenuOpen }) {
  const location = useLocation();

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname, setIsMenuOpen]);

  useEffect(() => {
    document.title = getPageTitle(location.pathname);
  }, [location.pathname]);

  return (
    <PageReadyProvider pathname={location.pathname}>
      <div className="App">
        <RouteTransition />
        <Navbar isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
        <AnimatePresence mode="wait" initial={false}>
          <motion.main
            className="page-stage"
            key={location.pathname}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={pageVariants}
            transition={pageTransition}
          >
            <Routes location={location}>
              <Route path="/" element={<><Hero /><Products /><About /><Contact /></>} />
              <Route path="/products" element={<ProductsRoutePage />} />
              <Route path="/about" element={<AboutRoutePage />} />
              <Route path="/contact" element={<ContactRoutePage />} />
              <Route path="/faq" element={<FaqRoutePage />} />
              <Route path="/support" element={<SupportRoutePage />} />
              <Route path="/blinds" element={<CategoryRoutePage category="BLINDS" title="Blinds" description="Compare our blind collections and jump into the product pages that fit your space." />} />
              <Route path="/shutters" element={<CategoryRoutePage category="SHUTTERS" title="Shutters" description="Explore our shutter options, from classic plantation styles to premium Eclipse® systems." />} />
              <Route path="/shades" element={<CategoryRoutePage category="SHADES" title="Shades" description="Review our shade collections and find a clean, modern look for each room." />} />
              <Route path="/product/:productId" element={<ProductPage />} />
            </Routes>
          </motion.main>
        </AnimatePresence>
        <Footer />
      </div>
    </PageReadyProvider>
  );
}

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <BrowserRouter>
      <AppShell isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
    </BrowserRouter>
  );
}

export default App;
